import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { BACKEND_URL } from 'AppSettings'
import store from 'app/store'
import { ApolloQueryResult } from '@apollo/client'
import { client } from 'app/App'

import {
  CourseInviteProvider,
  CourseInvite,
  CourseOrderItem,
  CourseDashboardResult,
  CourseType,
  CourseStatus,
  CoursePaymentType,
  CourseMethodType,
  CourseDiscInstUrlInfo,
  UpdateCourseDiscInstUrlInfoWithRegistration,
  isCourseChoosen
} from 'share/domain/course'
import {
  GetCourseDashboardResponse,
  GET_COURSE_DISC_INST_URL_INFO,
  GetCourseDiscInstUrlInfoVariables,
  GetCourseDiscInstUrlInfoResponse
} from 'share/queries/courses'

import {
  saveActiveCourseToSessionStorage,
  saveIsActiveCourseToSessionStorage,
  saveActiveTabToSessionStorage,
  saveTitleToSessionStorage,
  saveErrorMessageToSessionStorage,
  saveCourseInvitesToSessionStorageMain,
  saveCourseOrderItemsToSessionStorageMain,
  saveCourseDiscInstUrlInfoToSessionStorageMain,
  saveSelectedCourseToSessionStorageMain,
  saveVerifiedDetailsTrueToSessionStorage,
  saveVerifiedCoDebtorDetailsTrueToSessionStorage,
  saveAcceptedDisclosureAgreementTrueToSessionStorage,
  savehownCourseInstructionsTrueToSessionStorage,
  saveUpdateCourseDiscInstUrlInfoFieldsToSessionStorage
} from 'reducers/activecourse/activecourseSessionStorage'
import { RootState } from 'app/rootReducer'

export interface ActiveCourse {
  activeTab: 'first' | 'second'
  discInstUrlInfo: CourseDiscInstUrlInfo[]
  errorMessage: string | null
  inviteData: CourseInvite[]
  isActiveCourse: boolean
  isLoadingCourse: boolean
  orderData: CourseOrderItem[]
  selectedCourse: CourseInviteProvider | null
  title: string | null
}

const initialState: ActiveCourse = {
  activeTab: 'first',
  discInstUrlInfo: [],
  errorMessage: null,
  inviteData: [],
  isActiveCourse: false,
  isLoadingCourse: false,
  orderData: [],
  selectedCourse: null,
  title: null
}

export function getInactiveCourse(): ActiveCourse {
  const data: ActiveCourse = {
    activeTab: 'first',
    discInstUrlInfo: [],
    errorMessage: null,
    inviteData: [],
    isActiveCourse: false,
    isLoadingCourse: false,
    orderData: [],
    selectedCourse: null,
    title: null
  }
  return data
}

export function getEmptyCourseInvite(): CourseInvite {
  const data: CourseInvite = {
    orderId: 0,
    courseNumber: '',
    coursePaymentType: CoursePaymentType.ClientPays,
    courseProviders: []
  }
  return data
}

export function getEmptyCourseInviteProvider(): CourseInviteProvider {
  const data: CourseInviteProvider = {
    orderItemId: null,
    productKey: 0,
    name: '',
    price: '',
    valueProps: ''
  }
  return data
}

export function getEmptyCourseOrderItem(): CourseOrderItem {
  const data: CourseOrderItem = {
    courseNumber: '',
    orderId: 0,
    orderItemId: 0,
    isJoint: false,
    providerId: 0,
    providerName: '',
    courseType: CourseType.CreditCounseling,
    courseStatus: CourseStatus.ChooseCourse,
    courseMethod: CourseMethodType.Online,
    coursePaymentType: CoursePaymentType.ClientPays,
    coursePrice: '',
    coursePhoneNumber: '',
    courseAccessCode: '',
    courseCompletionDate: '',
    courseCertificationExpirationDate: '',
    verifiedDetails: false,
    verifiedCoDebtorDetails: false,
    acceptedDisclosureAgreement: false,
    shownCourseInstructions: false,
    hasRegisteredCourse: false
  }
  return data
}

export function getEmptyDiscInstUrlInfo(): CourseDiscInstUrlInfo {
  const data: CourseDiscInstUrlInfo = {
    courseNumber: '',
    courseOrderItemId: 0,
    courseUrl: '',
    instructions: '',
    disclosureContent: '',
    disclosureVersion: 0,
    info1: null,
    info2: null
  }
  return data
}

export function getEmptyCourseDashboardResult(): CourseDashboardResult {
  const data: CourseDashboardResult = {
    invites: [],
    orders: []
  }
  return data
}

function loadCourseInviteDataFromResponse(
  invites: CourseInvite[]
): CourseInvite[] {
  const courseInviteRecords: CourseInvite[] = []
  if (invites.length > 0) {
    invites.forEach((respCourseInvite) => {
      const newCourseInvite = getEmptyCourseInvite()
      newCourseInvite.orderId = respCourseInvite.orderId
      newCourseInvite.courseNumber = respCourseInvite.courseNumber
      newCourseInvite.coursePaymentType = respCourseInvite.coursePaymentType
      respCourseInvite.courseProviders.forEach((respCourseProvider) => {
        const newCourseProvider = getEmptyCourseInviteProvider()
        newCourseProvider.orderItemId = respCourseProvider.orderItemId
        newCourseProvider.productKey = respCourseProvider.productKey
        newCourseProvider.name = respCourseProvider.name
        newCourseProvider.price = respCourseProvider.price
        newCourseProvider.valueProps = respCourseProvider.valueProps
        newCourseInvite.courseProviders.push(newCourseProvider)
      })
      courseInviteRecords.push(newCourseInvite)
    })
  }

  return courseInviteRecords
}

function loadCourseOrderDataFromResponse(
  orders: CourseOrderItem[]
): CourseOrderItem[] {
  const newRecord: CourseOrderItem[] = []
  if (orders.length > 0) {
    orders.forEach((respOrderItem) => {
      const newOrderItem = getEmptyCourseOrderItem()
      newOrderItem.courseNumber = respOrderItem.courseNumber
      newOrderItem.orderId = respOrderItem.orderId
      newOrderItem.orderItemId = respOrderItem.orderItemId
      newOrderItem.isJoint = respOrderItem.isJoint
      newOrderItem.providerId = respOrderItem.providerId
      newOrderItem.providerName = respOrderItem.providerName
      newOrderItem.courseType = respOrderItem.courseType
      newOrderItem.courseStatus = respOrderItem.courseStatus
      newOrderItem.courseMethod = respOrderItem.courseMethod
      newOrderItem.coursePaymentType = respOrderItem.coursePaymentType
      newOrderItem.coursePrice = respOrderItem.coursePrice
      newOrderItem.coursePhoneNumber = respOrderItem.coursePhoneNumber
      newOrderItem.courseAccessCode = respOrderItem.courseAccessCode
      newOrderItem.courseCompletionDate = respOrderItem.courseCompletionDate
      newOrderItem.courseCertificationExpirationDate =
        respOrderItem.courseCertificationExpirationDate
      newOrderItem.verifiedDetails = respOrderItem.verifiedDetails
      newOrderItem.verifiedCoDebtorDetails =
        respOrderItem.verifiedCoDebtorDetails
      newOrderItem.acceptedDisclosureAgreement =
        respOrderItem.acceptedDisclosureAgreement
      newOrderItem.shownCourseInstructions =
        respOrderItem.shownCourseInstructions
      newOrderItem.hasRegisteredCourse = respOrderItem.hasRegisteredCourse
      newRecord.push(newOrderItem)
    })
  }

  return newRecord
}

export function loadCourseDashboardFromResponse(
  resp: GetCourseDashboardResponse
): CourseDashboardResult {
  const result = getEmptyCourseDashboardResult()

  result.invites = loadCourseInviteDataFromResponse(
    resp.orders.courseDashboard.invites
  )

  result.orders = loadCourseOrderDataFromResponse(
    resp.orders.courseDashboard.orders
  )

  return result
}

export function loadCourseDiscInstUrlInfoDataFromResponse(
  resp: GetCourseDiscInstUrlInfoResponse
): CourseDiscInstUrlInfo {
  const newRecord: CourseDiscInstUrlInfo = getEmptyDiscInstUrlInfo()

  if (resp.orders.courseDiscInstUrlInfo) {
    newRecord.courseNumber = resp.orders.courseDiscInstUrlInfo.courseNumber
    newRecord.courseOrderItemId =
      resp.orders.courseDiscInstUrlInfo.courseOrderItemId
    newRecord.courseUrl = resp.orders.courseDiscInstUrlInfo.courseUrl
    newRecord.disclosureContent =
      resp.orders.courseDiscInstUrlInfo.disclosureContent
    newRecord.disclosureVersion =
      resp.orders.courseDiscInstUrlInfo.disclosureVersion
    newRecord.instructions = resp.orders.courseDiscInstUrlInfo.instructions
    newRecord.info1 = resp.orders.courseDiscInstUrlInfo.info1
    newRecord.info2 = resp.orders.courseDiscInstUrlInfo.info2
  }
  return newRecord
}

const activeCourse = createSlice({
  name: 'activeCourse',
  initialState: initialState,
  reducers: {
    setActiveCourse(_state, { payload }: PayloadAction<ActiveCourse>) {
      saveActiveCourseToSessionStorage(payload)
      return payload
    },
    makeCourseActive(state) {
      state.isActiveCourse = true
      saveIsActiveCourseToSessionStorage(state.isActiveCourse)
    },
    makeCourseInActive(state) {
      state.isActiveCourse = false
      saveIsActiveCourseToSessionStorage(state.isActiveCourse)
    },
    setTabFirst(state) {
      state.activeTab = 'first'
      saveActiveTabToSessionStorage(state.activeTab)
    },
    setTabSecond(state) {
      state.activeTab = 'second'
      saveActiveTabToSessionStorage(state.activeTab)
    },
    setTitle(state, { payload }: PayloadAction<string>) {
      saveTitleToSessionStorage(payload)
      state.title = payload
    },
    setErrorMessage(state, { payload }: PayloadAction<string | null>) {
      saveErrorMessageToSessionStorage(payload)
      state.errorMessage = payload
    },
    setInviteData(state, { payload }: PayloadAction<CourseInvite[]>) {
      saveCourseInvitesToSessionStorageMain(payload)
      state.inviteData = payload
    },
    setOrderData(state, { payload }: PayloadAction<CourseOrderItem[]>) {
      saveCourseOrderItemsToSessionStorageMain(payload)
      state.orderData = payload
    },
    addCourseDiscInstUrlInfo(
      state,
      { payload }: PayloadAction<CourseDiscInstUrlInfo>
    ) {
      let found = false
      for (let index = 0; index < state.discInstUrlInfo.length; index++) {
        if (
          state.discInstUrlInfo[index].courseNumber === payload.courseNumber
        ) {
          state.discInstUrlInfo[index].info1 = payload.info1
          state.discInstUrlInfo[index].info2 = payload.info2
          state.discInstUrlInfo[index].courseUrl = payload.courseUrl
          state.discInstUrlInfo[index].courseOrderItemId =
            payload.courseOrderItemId
          state.discInstUrlInfo[index].disclosureContent =
            payload.disclosureContent
          state.discInstUrlInfo[index].disclosureVersion =
            payload.disclosureVersion
          state.discInstUrlInfo[index].instructions = payload.instructions
          found = true
        }
      }
      if (!found) {
        state.discInstUrlInfo.push(payload)
      }

      saveCourseDiscInstUrlInfoToSessionStorageMain(state.discInstUrlInfo)
    },
    setSelectedCourse(state, { payload }: PayloadAction<CourseInviteProvider>) {
      state.selectedCourse = payload
      saveSelectedCourseToSessionStorageMain(payload)
    },
    clearSelectedCourse(state) {
      state.selectedCourse = null
      saveSelectedCourseToSessionStorageMain(state.selectedCourse)
    },
    clearActiveCourse() {
      const inActiveCourse = getInactiveCourse()
      saveActiveCourseToSessionStorage(inActiveCourse)
      return inActiveCourse
    },
    setVerifiedDetailsTrue(state, { payload }: PayloadAction<string>) {
      for (let index = 0; index < state.orderData.length; index++) {
        if (state.orderData[index].courseNumber === payload) {
          state.orderData[index].verifiedDetails = true
          saveVerifiedDetailsTrueToSessionStorage(index)
          break
        }
      }
    },
    setVerifiedCoDebtorDetailsTrue(state, { payload }: PayloadAction<string>) {
      for (let index = 0; index < state.orderData.length; index++) {
        if (state.orderData[index].courseNumber === payload) {
          state.orderData[index].verifiedCoDebtorDetails = true
          saveVerifiedCoDebtorDetailsTrueToSessionStorage(index)
          break
        }
      }
    },
    setAcceptedDisclosureAgreementTrue(
      state,
      { payload }: PayloadAction<string>
    ) {
      for (let index = 0; index < state.orderData.length; index++) {
        if (state.orderData[index].courseNumber === payload) {
          state.orderData[index].acceptedDisclosureAgreement = true
          saveAcceptedDisclosureAgreementTrueToSessionStorage(index)
          break
        }
      }
    },
    setShownCourseInstructionsTrue(state, { payload }: PayloadAction<string>) {
      for (let index = 0; index < state.orderData.length; index++) {
        if (state.orderData[index].courseNumber === payload) {
          state.orderData[index].shownCourseInstructions = true
          savehownCourseInstructionsTrueToSessionStorage(index)
          break
        }
      }
    },
    setCourseDiscInstUrlInfoFields(
      state,
      { payload }: PayloadAction<UpdateCourseDiscInstUrlInfoWithRegistration>
    ) {
      for (let index = 0; index < state.discInstUrlInfo.length; index++) {
        if (
          state.discInstUrlInfo[index].courseNumber === payload.courseNumber
        ) {
          state.discInstUrlInfo[index].info1 = payload.info1
          state.discInstUrlInfo[index].info2 = payload.info2
          state.discInstUrlInfo[index].courseUrl = payload.courseUrl

          saveUpdateCourseDiscInstUrlInfoFieldsToSessionStorage(index, payload)
          break
        }
      }
      for (let index = 0; index < state.orderData.length; index++) {
        if (state.orderData[index].courseNumber == payload.courseNumber) {
          if (payload.courseStatus > 0) {
            state.orderData[index].courseStatus = payload.courseStatus
          }
          break
        }
      }
    },
    setCourseLoading(state, { payload }: { payload: boolean }) {
      state.isLoadingCourse = payload
    }
  }
})

export const {
  setActiveCourse,
  makeCourseActive,
  makeCourseInActive,
  setTabFirst,
  setTabSecond,
  setTitle,
  setErrorMessage,
  setInviteData,
  setOrderData,
  addCourseDiscInstUrlInfo,
  setSelectedCourse,
  clearSelectedCourse,
  clearActiveCourse,
  setVerifiedDetailsTrue,
  setVerifiedCoDebtorDetailsTrue,
  setAcceptedDisclosureAgreementTrue,
  setShownCourseInstructionsTrue,
  setCourseDiscInstUrlInfoFields,
  setCourseLoading
} = activeCourse.actions

export default activeCourse.reducer

// selectors

async function getCourseDiscInstUrlInfoHelper(
  input: GetCourseDiscInstUrlInfoVariables
): Promise<ApolloQueryResult<GetCourseDiscInstUrlInfoResponse>> {
  return client.query<
    GetCourseDiscInstUrlInfoResponse,
    GetCourseDiscInstUrlInfoVariables
  >({
    query: GET_COURSE_DISC_INST_URL_INFO,
    variables: input,
    fetchPolicy: 'no-cache'
  })
}

export function getCourseOrderItemByCourseNumberHelper(
  activeCourse: ActiveCourse,
  courseNumber: 'first' | 'second'
): CourseOrderItem | null {
  let courseOrderItem: CourseOrderItem | null = null
  for (let index = 0; index < activeCourse.orderData.length; index++) {
    if (activeCourse.orderData[index].courseNumber === courseNumber) {
      courseOrderItem = activeCourse.orderData[index]
      break
    }
  }
  return courseOrderItem
}

export function getCourseInviteByCourseNumberHelper(
  activeCourse: ActiveCourse,
  courseNumber: 'first' | 'second'
): CourseInvite | null {
  let courseInvite: CourseInvite | null = null
  for (let index = 0; index < activeCourse.inviteData.length; index++) {
    if (activeCourse.inviteData[index].courseNumber === courseNumber) {
      courseInvite = activeCourse.inviteData[index]
      break
    }
  }
  return courseInvite
}

export const getActiveDiscInstUrlInfo = createSelector(
  (state: RootState) => state.activeCase,
  (state: RootState) => state.activeCourse,
  (activeCase, activeCourse): CourseDiscInstUrlInfo | null => {
    let courseDiscInstUrlInfo: CourseDiscInstUrlInfo | null = null
    for (let index = 0; index < activeCourse.discInstUrlInfo.length; index++) {
      if (
        activeCourse.discInstUrlInfo[index].courseNumber ===
        activeCourse.activeTab
      ) {
        courseDiscInstUrlInfo = activeCourse.discInstUrlInfo[index]
        break
      }
    }

    if (!courseDiscInstUrlInfo) {
      const orderItem = getCourseOrderItemByCourseNumberHelper(
        activeCourse,
        activeCourse.activeTab
      )
      if (
        orderItem &&
        activeCase &&
        activeCase.matter &&
        isCourseChoosen(orderItem)
      ) {
        const input: GetCourseDiscInstUrlInfoVariables = {
          request: {
            caseId: activeCase.matter.id,
            orderId: orderItem.orderId,
            courseType: orderItem.courseType,
            courseMethodType: orderItem.courseMethod
          }
        }

        getCourseDiscInstUrlInfoHelper(input)
          .then((result) => {
            if (result.data) {
              const discInstUrlInfoData =
                loadCourseDiscInstUrlInfoDataFromResponse(result.data)
              const info1 = discInstUrlInfoData.info1
                ? discInstUrlInfoData.info1
                : 'noinfo1'
              const info2 = discInstUrlInfoData.info2
                ? discInstUrlInfoData.info2
                : 'noinfo2'
              discInstUrlInfoData.courseUrl = `${BACKEND_URL}/orders/courses/Provider${orderItem.providerId}?info1=${info1}&info2=${info2}`
              store.dispatch(addCourseDiscInstUrlInfo(discInstUrlInfoData))
            }
          })
          .catch((err) => {
            console.log('getCourseDiscInstUrlInfoHelper had err', err)
          })
      }
    }

    return courseDiscInstUrlInfo
  }
)

export const getFirstCourseOrderItem = createSelector(
  (state: RootState) => state.activeCourse,
  (activeCourse): CourseOrderItem | null => {
    return getCourseOrderItemByCourseNumberHelper(activeCourse, 'first')
  }
)

export const getSecondCourseOrderItem = createSelector(
  (state: RootState) => state.activeCourse,
  (activeCourse): CourseOrderItem | null => {
    return getCourseOrderItemByCourseNumberHelper(activeCourse, 'second')
  }
)

export const getActiveTabCourseOrderItem = createSelector(
  (state: RootState) => state.activeCourse,
  (activeCourse): CourseOrderItem | null => {
    return getCourseOrderItemByCourseNumberHelper(
      activeCourse,
      activeCourse.activeTab
    )
  }
)

export const getActiveTabCourseInvite = createSelector(
  (state: RootState) => state.activeCourse,
  (activeCourse): CourseInvite | null => {
    return getCourseInviteByCourseNumberHelper(
      activeCourse,
      activeCourse.activeTab
    )
  }
)
